import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import Input from '../components/Input';
import Button from '../components/Button';
import logo from '../assets/images/logo.png';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { IoMdMail } from "react-icons/io";
import { RiLockPasswordFill } from "react-icons/ri";
import { validateRequired, validateEmail } from '../utils/validations';
import { toastError, toastSuccess } from '../utils/toast';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState({ email: '', password: '' });

  const validateEmailField = () => {
    const err = validateRequired(email) || validateEmail(email);
    setFormError(prev => ({ ...prev, email: err || '' }));
    return !err;
  };

  const validatePasswordField = () => {
    const err = validateRequired(password);
    setFormError(prev => ({ ...prev, password: err || '' }));
    return !err;
  };

  const validateAll = () => validateEmailField() & validatePasswordField();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateAll()) return;
    setLoading(true);
    try {
      const user = await login(email, password);
      toastSuccess('Bienvenido ' + (user.user_metadata?.full_name || user.email));
    } catch (err) {
      toastError(err.message || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.08, delayChildren: 0.15 } },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 overflow-hidden"
      style={{ backgroundColor: '#0a0b16', position: 'relative' }}
    >
      {/* Dot grid background */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
        backgroundSize: '28px 28px',
      }} />

      {/* Ambient orbs */}
      <motion.div
        animate={{ opacity: [0.7, 1, 0.7] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          position: 'absolute', top: '-80px', left: '-100px',
          width: '420px', height: '420px', borderRadius: '50%',
          background: 'rgba(220,31,38,0.14)',
          filter: 'blur(120px)',
          pointerEvents: 'none',
          animation: 'float 14s ease-in-out infinite',
        }}
      />
      <motion.div
        style={{
          position: 'absolute', bottom: '-80px', right: '-100px',
          width: '380px', height: '380px', borderRadius: '50%',
          background: 'rgba(59,130,246,0.14)',
          filter: 'blur(120px)',
          pointerEvents: 'none',
          animation: 'float 18s ease-in-out infinite reverse',
        }}
      />
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%,-50%)',
        width: '300px', height: '300px', borderRadius: '50%',
        background: 'rgba(0,194,255,0.07)',
        filter: 'blur(100px)',
        pointerEvents: 'none',
        animation: 'float 11s ease-in-out infinite',
      }} />

      {/* Gradient border card */}
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        style={{
          position: 'relative', zIndex: 1,
          width: '100%', maxWidth: '420px',
          background: 'linear-gradient(90deg, #DC1F26 0%, #3B82F6 60%, #00C2FF 100%)',
          backgroundSize: '200% 200%',
          animation: 'gradient-shift 8s ease infinite',
          borderRadius: '16px',
          padding: '1px',
        }}
      >
        <div style={{
          background: '#12132a',
          borderRadius: '15px',
          backdropFilter: 'blur(16px)',
          overflow: 'hidden',
        }}>
          <motion.div
            className="p-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Logo */}
            <motion.div variants={itemVariants} className="flex justify-center mb-6">
              <img src={logo} alt="Logo" className="sidebar-logo w-40 object-contain" />
            </motion.div>

            {/* Title */}
            <motion.div variants={itemVariants} className="mb-8 text-center">
              <h2 className="text-2xl font-extrabold" style={{ color: '#E8EAF0' }}>
                Inicia sesión
              </h2>
              <p className="mt-1 text-sm" style={{ color: '#4A5270' }}>
                Accede a tu panel de administración
              </p>
            </motion.div>

            {/* Form */}
            <form onSubmit={handleSubmit} noValidate>
              <motion.div variants={itemVariants}>
                <Input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  placeholder="user@mail.com"
                  value={email}
                  label="Correo electrónico"
                  error={formError.email}
                  leftIcon={<IoMdMail />}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    validateEmailField();
                  }}
                />
              </motion.div>

              <motion.div variants={itemVariants}>
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  placeholder="**********"
                  value={password}
                  label="Contraseña"
                  error={formError.password}
                  leftIcon={<RiLockPasswordFill />}
                  rightIcon={showPassword ? <FaEyeSlash /> : <FaEye />}
                  onRightIconClick={() => setShowPassword(!showPassword)}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    validatePasswordField();
                  }}
                />
              </motion.div>

              {error && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-red-500 text-sm text-center mb-4"
                >
                  {error}
                </motion.div>
              )}

              <motion.div variants={itemVariants} className="mt-2">
                <Button
                  type="submit"
                  className="rounded-[40px]"
                  loading={loading}
                  variant="primary"
                  fullWidth
                  size="md"
                >
                  Iniciar sesión
                </Button>
              </motion.div>
            </form>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
